import useRestService from '../shared/rest-service';
import * as React from 'react';
import Dashboard from '../models/dashboard';
import useTag from './useTag';
import Tag from '../models/tag';
import { TableauDesktopOptions } from '../models/tableau';

/**
 * useDashboard
 * @function useDashboard - Custom hook for the user management page
 */
const useDashboardManagement = () => {
  const { getAuth, postAuth, putAuth } = useRestService();

  /**
   * @function createDashboard - Create a new dashboard
   * @param dashboard - the new dashboard to create
   */
  const createDashboard = (clientId: number, dashboard: Dashboard) => {
    dashboard.status = true;
    dashboard.reportId = 0;

    return postAuth(`api/${clientId}/dashboards`, dashboard);
  };

  /**
   * @function fetchDashboard - Create a new dashboard
   * @param clientId - the client ID to fetch a dashboard from
   * @param dashboardId - the dashboard ID to fetch the dashboard of
   */
  const fetchDashboard = (clientId: number, dashboardId: number) => {
    return getAuth(`api/${clientId}/dashboards/${dashboardId}`);
  };

  const [loadingAssociatedData, setLoadingAssociatedData] = React.useState<boolean>(true);
  const [tags, setTags] = React.useState<Tag[]>([]);
  const { fetchTags } = useTag();

  /**
   * @function fetchAssociatedData - Get extra data needed to create/edit a user
   */
  const fetchAssociatedData = (clientId: number) => {
    if (!loadingAssociatedData) setLoadingAssociatedData(true);
    Promise.all([fetchTags(clientId)]).then(([tags]) => {
      setTags(tags);
      setLoadingAssociatedData(false);
    });
  };

  /**
   * @function editDashboard - Edit a dashboard
   * @param clientId - the client id the dashboard belongs to
   * @param dashboard - the updated dashboard
   */
  const editDashboard = (clientId: number, dashboard: Dashboard) => {
    return putAuth(`api/${clientId}/dashboards/${dashboard.reportId}`, dashboard);
  };

  /**
   * fetchAllDashboards
   * @function fetchAllDashboards - Fetches the dashboards for a single client
   * @param clientId - the client id
   */
  const fetchAllDashboards = (clientId: number) => {
    let queryParams = `?page=1&pageSize=0`;
    return getAuth(`api/${clientId}/dashboards${queryParams}`);
  };

  const fetchAllDashboardsFromTableau = (clientId: number): Promise<TableauDesktopOptions[]> => {
    return getAuth(`api/${clientId}/dashboards/tableau/list`);
  };

  return {
    createDashboard,
    fetchDashboard,
    editDashboard,
    loadingAssociatedData,
    tags,
    fetchAssociatedData,
    fetchAllDashboards,
    fetchAllDashboardsFromTableau
  };
};

export default useDashboardManagement;
